import React, { useEffect, useState } from "react";
import api, { getToken, API } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, CheckCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function UserQR() {
  const [dash, setDash] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/user/dashboard").then(({ data }) => setDash(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let revoke;
    if (dash?.has_qr) {
      api.get("/user/qr", { responseType: "blob" }).then((r) => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setImgUrl(url);
      }).catch(() => {});
    }
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [dash]);

  const upi = dash?.upi_id || "";
  const upiLink = upi ? `upi://pay?pa=${encodeURIComponent(upi)}&pn=NaxoPay&cu=INR` : "";

  const copyUpi = () => {
    navigator.clipboard.writeText(upi);
    setCopied(true);
    toast.success("UPI ID copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const downloadQr = () => {
    if (imgUrl) {
      const a = document.createElement("a");
      a.href = imgUrl;
      a.download = "naxopay-qr.png";
      a.click();
    } else {
      const canvas = document.querySelector("#vp-qr-canvas");
      if (canvas) {
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = "naxopay-qr.png";
        a.click();
      }
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="qr-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">Collect payment</h1>
      <p className="text-sm text-slate-500 mb-6">Share this QR with your customers to receive UPI payments.</p>

      {/* The QR card */}
      <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 border border-indigo-500/30" data-testid="static-qr-card">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-indigo-500/20 blur-2xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-600 grid place-items-center font-heading font-extrabold">V</div>
            <span className="font-heading font-bold">NaxoPay</span>
          </div>
          <span className="text-xs text-indigo-200 bg-white/10 px-2 py-1 rounded-full">UPI Accepted</span>
        </div>

        <div className="relative mt-6 bg-white rounded-2xl p-5 flex items-center justify-center">
          {dash?.has_qr && imgUrl ? (
            <img src={imgUrl} alt="Static QR" className="w-full max-w-[240px] rounded-lg" data-testid="qr-image" />
          ) : upi ? (
            <QRCodeCanvas id="vp-qr-canvas" value={upiLink} size={240} level="M" includeMargin data-testid="qr-generated" />
          ) : (
            <div className="py-16 text-center text-slate-400 text-sm">QR not assigned yet.<br />Please contact admin.</div>
          )}
        </div>

        {upi && (
          <div className="relative mt-5">
            <div className="text-xs text-indigo-200 mb-1">UPI ID</div>
            <div className="flex items-center justify-between gap-2 bg-white/10 rounded-xl px-4 py-3">
              <span className="font-mono-data text-sm truncate" data-testid="upi-id-value">{upi}</span>
              <button onClick={copyUpi} data-testid="static-qr-copy-upi-btn" className="shrink-0 text-indigo-200 hover:text-white">
                {copied ? <CheckCheck className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        <div className="relative mt-5 flex items-center justify-center gap-4 text-[11px] text-indigo-200">
          <span>GPay</span><span>•</span><span>PhonePe</span><span>•</span><span>Paytm</span><span>•</span><span>BHIM</span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <Button onClick={copyUpi} variant="outline" className="rounded-xl h-11" disabled={!upi} data-testid="copy-upi-btn-2">
          <Copy className="h-4 w-4 mr-2" /> Copy UPI
        </Button>
        <Button onClick={downloadQr} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700" disabled={!dash?.has_qr && !upi} data-testid="download-qr-btn">
          <Download className="h-4 w-4 mr-2" /> Download QR
        </Button>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-sm text-slate-500">
        <Smartphone className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
        <span>Customers scan this QR with any UPI app and pay. Credits are added to your collection wallet by admin after UTR verification — your dashboard updates automatically with a sound alert.</span>
      </div>
    </div>
  );
}
